import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, FileText, Globe, Languages, Sparkles } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { PageGallery } from "@/components/PageGallery";
import { COMPANY } from "@/lib/company";
import type { TranslationLang } from "@/content/translation-langs/registry";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import {
  buildBreadcrumbSchema,
  buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";

function buildFaqs(l: TranslationLang) {
  return [
    {
      q: `แปล${l.nameTh}รับรอง ราคาเท่าไหร่?`,
      a: `เริ่มต้น ${l.pricePerPage.from.toLocaleString()} บาท/หน้า A4 ภาษาทั่วไป สูงสุด ${l.pricePerPage.to.toLocaleString()} บาท สำหรับเอกสารเฉพาะทาง (แพทย์, กฎหมาย, เทคนิค) ทุกฉบับรวมการรับรองทนาย Notarial Services Attorney และสามารถ add-on MFA + สถานทูตได้ครบจบ`,
    },
    {
      q: `ใช้เวลานานแค่ไหน?`,
      a: `${l.turnaround} ส่งสแกนก่อนเที่ยงเริ่มงานบ่ายวันเดียวกัน — ถ้าด่วน last-minute (ก่อนบิน) แจ้งทีมไลน์ ${COMPANY.lineId} เพื่อจัดคิวพิเศษได้`,
    },
    {
      q: `${l.nameEn} (${l.nativeName}) translation ใช้ที่ไหนได้บ้าง?`,
      a: `รับรองโดย ${l.certAuthority} ใช้ยื่นได้ที่: ${l.acceptedIn.join(", ")} ครอบคลุมหน่วยงานราชการ ศาลากลาง สถานทูต มหาวิทยาลัย และนายจ้างปลายทาง`,
    },
    {
      q: `เอกสารแบบไหนแปลได้บ้าง?`,
      a: `${l.popularDocs.join(" · ")} และเอกสารราชการ/บริษัทอื่นๆ ทุกประเภท — แจ้งเอกสารที่ต้องการแปลทางไลน์ ${COMPANY.lineId} เพื่อรับใบเสนอราคาภายใน 30 นาที`,
    },
    {
      q: `รับ MFA + รับรองสถานทูตให้ครบจบเลยไหม?`,
      a: `ใช่ — ทีมเดิน MFA (แจ้งวัฒนะ) และสถานทูตปลายทางในกรุงเทพให้เสร็จในจุดเดียว ไม่ต้องเดินทางเอง รับเอกสาร end-to-end พร้อมจัดส่งกลับทั่วไทยและส่ง DHL/EMS ไปต่างประเทศได้`,
    },
    {
      q: `ต่างจากร้านแปลทั่วไปยังไง?`,
      a: `ทุกฉบับแปลโดยเจ้าของภาษา ตรวจคุณภาพ 3 ชั้น (Translator → Senior Reviewer → Notary Lawyer) แนบ Translation Certificate ลงนามและประทับตราทนายที่ขึ้นทะเบียนสภาทนายความฯ — การรับเอกสารเป็นดุลพินิจของกรมการกงสุลและสถานทูตปลายทาง เราไม่รับประกันผล แต่หากถูกตีกลับจากความผิดพลาดของผู้แปล เราแก้ไขให้ฟรีรวมค่าวิ่งเอกสาร`,
    },
  ];
}

export const Route = createFileRoute("/translation/$lang")({
  loader: async ({
    params,
  }): Promise<{
    lang: TranslationLang;
    related: { slug: string; nameTh: string; nameEn: string; flag: string; priceFrom: number }[];
  }> => {
    const { getTranslationLang, TRANSLATION_LANGS } = await import(
      "@/content/translation-langs/registry"
    );
    const l = getTranslationLang(params.lang);
    if (!l) throw notFound();
    return {
      lang: l,
      related: TRANSLATION_LANGS.filter((x) => x.slug !== l.slug)
        .slice(0, 6)
        .map((x) => ({ slug: x.slug, nameTh: x.nameTh, nameEn: x.nameEn, flag: x.flag, priceFrom: x.pricePerPage.from })),
    };
  },
  head: ({ params, loaderData }) => {
    const l = loaderData?.lang;
    if (!l) {
      return pageHeadForProvince({
        title: "ไม่พบภาษา",
        description: "ไม่พบภาษานี้",
        path: `/translation/${params.lang}`,
      });
    }
    const site = getActiveSiteUrl();
    const rawTitle = `แปล${l.nameTh}รับรอง — MFA + สถานทูต`;
    const title = rawTitle.length > 60 ? rawTitle.slice(0, 59) + "…" : rawTitle;
    const rawDesc = `แปล${l.nameTh} (${l.nameEn}) รับรองโดย ${l.certAuthority} เริ่ม ${l.pricePerPage.from} บาท/หน้า ${l.turnaround} ใช้ยื่น ${l.acceptedIn.slice(0, 3).join(", ")}`;
    const desc = rawDesc.length > 158 ? rawDesc.slice(0, 157) + "…" : rawDesc;
    const base = pageHeadForProvince({ title, description: desc, path: `/translation/${l.slug}` });
    const faqs = buildFaqs(l);
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "Translation", url: `${site}/translation` },
              { name: `แปล${l.nameTh}`, url: `${site}/translation/${l.slug}` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${site}/translation/${l.slug}#service`,
            name: `Certified ${l.nameEn} Translation (${l.nativeName})`,
            serviceType: "Certified Translation",
            provider: {
              "@type": "LegalService",
              name: COMPANY.legalName,
              telephone: COMPANY.phone,
              url: site,
              priceRange: "฿฿",
            },
            areaServed: l.acceptedIn.map((c) => ({ "@type": "Country", name: c })),
            availableLanguage: [
              { "@type": "Language", name: l.nameEn, alternateName: l.nativeName },
              { "@type": "Language", name: "Thai", alternateName: "ภาษาไทย" },
            ],
            offers: {
              "@type": "Offer",
              priceCurrency: "THB",
              price: l.pricePerPage.from,
              priceSpecification: {
                "@type": "PriceSpecification",
                minPrice: l.pricePerPage.from,
                maxPrice: l.pricePerPage.to,
                priceCurrency: "THB",
                unitText: "page",
              },
              availability: "https://schema.org/InStock",
            },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/translation/${params.lang}`)) },
      ],
    };
  },
  component: TranslationLangPage,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold">ไม่พบภาษานี้</h1>
        <Link to="/translation" className="mt-4 inline-flex items-center gap-1 text-gold">
          <ArrowLeft className="h-4 w-4" /> กลับรายการภาษา
        </Link>
      </div>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="p-10 text-center">
      <h1 className="text-xl font-bold">เกิดข้อผิดพลาด</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
});

function TranslationLangPage() {
  const data = Route.useLoaderData() as {
    lang: TranslationLang;
    related: { slug: string; nameTh: string; nameEn: string; flag: string; priceFrom: number }[];
  };
  const l = data.lang;
  const faqs = buildFaqs(l);
  const related = data.related;

  return (
    <div className="min-h-screen bg-background text-foreground" lang={l.langCode} dir={l.direction}>
      <SiteHeader />
      <main id="main-content" lang="th" dir="ltr">
        <nav className="border-b border-border bg-muted/30">
          <div className="mx-auto max-w-5xl px-4 py-3 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <span className="mx-2">/</span>
            <Link to="/translation" className="hover:text-primary">Translation</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">แปล{l.nameTh}</span>
          </div>
        </nav>

        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <div className="text-xs font-bold uppercase tracking-widest text-gold">
              <Languages className="mr-1 inline h-3.5 w-3.5" />
              {l.flag} {l.nameEn} · <span lang={l.langCode}>{l.nativeName}</span>
            </div>
            <h1 className="mt-2 text-3xl font-extrabold md:text-4xl">
              แปล{l.nameTh}รับรอง — MFA + สถานทูตในจุดเดียว
            </h1>
            <p id="ai-answer-tldr" className="mt-4 rounded-2xl border border-gold/30 bg-gold/5 p-5 text-base leading-relaxed">
              <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-gold">
                <Sparkles className="mr-1 inline h-3.5 w-3.5" /> AI Quick Answer
              </span>
              {l.aeoAnswer}
            </p>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">{l.intro}</p>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto grid max-w-5xl gap-3 px-4 py-8 md:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">ราคา</div>
              <div className="mt-1 text-lg font-extrabold text-gold">
                ฿{l.pricePerPage.from.toLocaleString()} – ฿{l.pricePerPage.to.toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground">ต่อหน้า A4</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">ส่งงาน</div>
              <div className="mt-1 text-sm font-bold">{l.turnaround}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">รับรองโดย</div>
              <div className="mt-1 text-sm font-bold">{l.certAuthority}</div>
            </div>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">ใช้แปล{l.nameTh}ทำอะไรได้บ้าง?</h2>
            <ul className="mt-5 grid gap-3 md:grid-cols-2">
              {l.useCases.map((u, i) => (
                <li key={i} className="flex items-start gap-2 rounded-xl border border-border bg-card p-4 text-sm">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span>{u}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">เอกสารที่แปลบ่อย</h2>
            <ul className="mt-5 grid gap-3 md:grid-cols-2">
              {l.popularDocs.map((d, i) => (
                <li key={i} className="flex items-start gap-2 rounded-xl border border-border bg-card p-4 text-sm">
                  <FileText className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">ใช้ที่ประเทศไหนได้บ้าง?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              แปลรับรองของเรา ยอมรับโดยหน่วยงานราชการในประเทศต่อไปนี้:
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {l.acceptedIn.map((c) => (
                <span key={c} className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold">
                  <Globe className="mr-1 inline h-3 w-3 text-gold" />
                  {c}
                </span>
              ))}
            </div>
            {l.countrySlugs.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-3">
                {l.countrySlugs.map((slug) => (
                  <Link
                    key={slug}
                    to="/countries/$country"
                    params={{ country: slug }}
                    className="rounded-md border border-gold/40 bg-gold/5 px-4 py-2 text-sm font-bold text-primary hover:bg-gold/10"
                  >
                    ดูคู่มือยื่นเอกสารที่ {slug} →
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-3xl px-4 py-12">
            <h2 className="text-2xl font-bold">คำถามที่พบบ่อย</h2>
            <div className="mt-5 space-y-3">
              {faqs.map((f, i) => (
                <details key={i} className="group rounded-xl border border-border bg-card p-4">
                  <summary className="cursor-pointer font-semibold text-primary">{f.q}</summary>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-xl font-bold">ภาษาอื่นที่นิยมแปล</h2>
            <div className="mt-5 grid gap-3 md:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to="/translation/$lang"
                  params={{ lang: r.slug }}
                  className="rounded-xl border border-border bg-card p-4 text-sm transition-all hover:-translate-y-0.5 hover:border-gold"
                >
                  <span className="text-xl">{r.flag}</span>
                  <div className="mt-1 font-bold text-primary">แปล{r.nameTh}</div>
                  <div className="text-xs text-muted-foreground">เริ่ม ฿{r.priceFrom.toLocaleString()}/หน้า</div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-4 py-12 text-center">
            <h2 className="text-2xl font-extrabold">พร้อมเริ่มงานแปล{l.nameTh}แล้วใช่ไหม?</h2>
            <p className="mt-3 text-sm opacity-90">
              ส่งสแกนเอกสารทางไลน์ ทีมแจ้งราคาและกำหนดส่งภายใน 30 นาที
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <a href={`tel:${COMPANY.phoneIntl}`} className="rounded-md bg-gold px-5 py-2.5 text-sm font-bold text-foreground">
                โทร {COMPANY.phone}
              </a>
              <a href={`https://line.me/R/ti/p/${COMPANY.lineId}`} target="_blank" rel="noopener" className="rounded-md border border-primary-foreground/30 bg-primary-foreground/10 px-5 py-2.5 text-sm font-bold">
                ทักไลน์ {COMPANY.lineId}
              </a>
            </div>
          </div>
        </section>
        <div className="mx-auto max-w-4xl px-4">
          <FaqSourceNote topic="translation" locale="th" />
        </div>
        <div className="mb-10">
          <PageGallery category="translation" seed="translation-lang" locale="th" />
        </div>

      </main>
      <SiteFooter />
    </div>
  );
}
